import { ChevronLeftIcon } from '@heroicons/react/16/solid';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { Breadcrumbs, Button } from '@/components/ui/daisy';
import { CompoundProps, WithClassname } from '@/components/ui/types';
import { BreadcrumbItem } from '@/data/hook/useDynamicBreadcrumbItems';

export type PageHeaderBackProps = WithClassname & {
  onClick: () => void;
};

type PageHeaderBreadcrumbsProps = WithClassname & {
  items: BreadcrumbItem[];
  itemClassName?: string;
  showBreadcrumbsOnMobile?: boolean;
};

const Root = ({ className, children }: CompoundProps) => (
  <header className={twMerge('flex flex-col gap-4', className)}>
    {children}
  </header>
);

const Row = ({ className, children }: CompoundProps) => (
  <div className={twMerge('flex flex-wrap items-center gap-4', className)}>
    {children}
  </div>
);

const Back = ({ onClick, className }: PageHeaderBackProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'common' });

  return (
    <Button
      variant="soft"
      size="sm"
      className={twMerge('lowercase', className)}
      onClick={onClick}
    >
      <ChevronLeftIcon className="size-3" />
      {t('back')}
    </Button>
  );
};

const Title = ({ className, children }: CompoundProps) => (
  <h1
    className={twMerge(
      'min-w-0 text-3xl font-semibold text-base-content',
      className,
    )}
  >
    {children}
  </h1>
);

const Actions = ({ className, children }: CompoundProps) => (
  <div
    className={twMerge('flex flex-col w-full md:w-auto md:ml-auto', className)}
  >
    {children}
  </div>
);

const isCurrentPage = (items: BreadcrumbItem[], index: number) =>
  items.length > 1 && index === items.length - 1;

const PageBreadcrumbs = ({
  items,
  className,
  itemClassName,
  showBreadcrumbsOnMobile = false,
}: PageHeaderBreadcrumbsProps) => (
  <Breadcrumbs
    className={twMerge(
      showBreadcrumbsOnMobile ? '' : 'hidden',
      'md:block',
      className,
    )}
  >
    {items.map((item, index) => (
      <Breadcrumbs.Item
        key={item.id}
        icon={item.icon}
        href={isCurrentPage(items, index) ? undefined : item.href}
        className={itemClassName}
      >
        {item.children}
      </Breadcrumbs.Item>
    ))}
  </Breadcrumbs>
);

Root.displayName = 'PageHeader';
Row.displayName = 'PageHeader.Row';
Back.displayName = 'PageHeader.Back';
Title.displayName = 'PageHeader.Title';
Actions.displayName = 'PageHeader.Actions';
PageBreadcrumbs.displayName = 'PageHeader.Breadcrumbs';

export const PageHeader = Object.assign(Root, {
  Row,
  Back,
  Title,
  Actions,
  Breadcrumbs: PageBreadcrumbs,
});
